import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import luxeLordsLogoDark from "@/assets/luxe-lords-logo-black.svg";
import luxeLordsLogoWhite from "@/assets/luxe-lords-logo-white.svg";
import { CartDrawer } from "./CartDrawer";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Shop", href: "/shop" },
    { label: "Craft", href: "#craft" },
    { label: "Story", href: "#story" },
    { label: "Contact", href: "/contact" },
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);

    if (href.startsWith("#")) {
      if (isHomePage) {
        // On homepage, scroll to section
        const element = document.querySelector(href);
        element?.scrollIntoView({ behavior: "smooth" });
      } else {
        // On other pages, navigate to homepage with hash
        navigate("/" + href);
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || !isHomePage
          ? "bg-background/95 backdrop-blur-md shadow-subtle"
          : "bg-gradient-to-b from-black/50 to-transparent"
      }`}
    >
      <nav className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-14 lg:h-16">
          {/* Logo - hidden on hero, visible when scrolled or on other pages */}
          <Link
            to="/"
            className={`transition-opacity duration-500 ${
              isScrolled || !isHomePage ? "opacity-100" : "md:opacity-0 opacity-100"
            }`}
          >
            <img 
              src={isScrolled || !isHomePage ? luxeLordsLogoDark : luxeLordsLogoWhite} 
              alt="Luxe Lords" 
              className="h-4 sm:h-5 lg:h-6 w-auto max-w-[140px] sm:max-w-[170px] lg:max-w-[200px] object-contain"
              style={{ imageRendering: 'auto' }}
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-12">
            {navLinks.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-luxury transition-colors duration-300 ${
                    isScrolled || !isHomePage
                      ? "text-foreground/80 hover:text-foreground"
                      : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className={`text-luxury transition-colors duration-300 ${
                    isScrolled || !isHomePage
                      ? "text-foreground/80 hover:text-foreground"
                      : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          {/* Cart & CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <CartDrawer onOpenChange={setIsCartOpen} className={isScrolled || !isHomePage ? "text-foreground bg-background/80 border-foreground/60 backdrop-blur-md hover:bg-muted" : "text-white bg-black/30 border-white/70 backdrop-blur-md shadow-[0_2px_10px_rgba(0,0,0,0.35)] hover:bg-black/45"} />
            {!location.pathname.startsWith("/product") && location.pathname !== "/shop" && (
              <button
                onClick={() => { setIsMobileMenuOpen(false); navigate("/shop"); }}
                className={`btn-luxury transition-opacity ${isCartOpen ? "invisible opacity-0" : ""}`}
              >
                Shop Now
              </button>
            )}
          </div>

          {/* Mobile Cart & Menu Button */}
          <div className={`md:hidden flex items-center gap-2 ${isScrolled || !isHomePage ? "text-foreground" : "text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"}`}>
            <CartDrawer className={isScrolled || !isHomePage ? "text-foreground bg-background/80 border-foreground/60 backdrop-blur-md hover:bg-muted" : "text-white bg-black/30 border-white/70 backdrop-blur-md shadow-[0_2px_10px_rgba(0,0,0,0.35)] hover:bg-black/45"} />
            <button
              className={`p-2`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-background border-t border-border animate-fade-in">
            <div className="container mx-auto px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) =>
                link.href.startsWith("/") ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-luxury text-foreground/80 hover:text-foreground transition-colors duration-300 py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    className="text-luxury text-foreground/80 hover:text-foreground transition-colors duration-300 py-2 text-left"
                  >
                    {link.label}
                  </button>
                )
              )}
              {!location.pathname.startsWith("/product") && location.pathname !== "/shop" && (
                <button
                  onClick={() => { setIsMobileMenuOpen(false); navigate("/shop"); }}
                  className="btn-luxury mt-4"
                >
                  Shop Now
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navigation;
